import React from 'react';
import { matchPath } from 'react-router-dom';

import IframeScreenPage from 'components/pages/IframeScreenPage';
import { IframeScreenConfig, ScreenRouteEntry, ScreenSubRouteEntry } from 'types/screenRoutes';
import { getConfig } from 'utils/helperMethods';

/** RGM landing page. A static route, so it survives `userAccessScope` route filtering. */
export const LANDING_ROUTE = '/home';

/**
 * Where "home" is for this tenant.
 *
 * Before the landing page existed, five call sites each spelled out
 * `screenDetailsWithRoutes[0].path` — the tenant's first configured route. That convention
 * stays for tenants without `commonConfig.useLandingPage`, so the flag is the only thing that
 * changes behaviour. Always absolute: config paths are stored bare, and only some of the old
 * call sites prepended the slash.
 */
export const resolveHomePath = (appConfig: any): string => {
    if (getConfig('commonConfig', appConfig)?.useLandingPage === true) return LANDING_ROUTE;
    const first = appConfig?.screenDetailsWithRoutes?.[0]?.path;
    return first ? `/${first}` : LANDING_ROUTE;
};

type ResolvableEntry = {
    pageId?: string;
    iframe?: IframeScreenConfig;
    label?: string;
    id?: string;
    path?: string;
};

// Picks the element for one config route entry. The component map is passed in rather than
// imported so this stays testable without pulling App.tsx's provider graph.
export const resolveRouteElement = (
    entry: ResolvableEntry,
    componentMapping: Record<string, React.ReactElement>,
): React.ReactElement | null => {
    const mapped = entry?.pageId ? componentMapping[entry.pageId] : undefined;
    if (mapped) return mapped;

    if (entry?.iframe?.url) return <IframeScreenPage entry={entry} />;

    return null;
};

// Config paths are stored without a leading slash; matchPath expects both sides absolute.
export const toAbsolute = (value: string): string => (value.startsWith('/') ? value : `/${value}`);

// Delegates to react-router-dom's own matcher (the same one <Route path> uses) instead of
// exact string equality, so this can never disagree with what actually got rendered - including
// on tenant paths with dynamic segments (e.g. ":id", ":mode/:projectId/scenario/:scenarioId").
// Longest pattern wins, so a sub-route beats its parent.
export const findScreenEntryByPath = (
    screens: ScreenRouteEntry[] | undefined,
    pathname: string,
): ScreenRouteEntry | ScreenSubRouteEntry | undefined => {
    if (!Array.isArray(screens)) return undefined;

    let match: ScreenRouteEntry | ScreenSubRouteEntry | undefined;
    let matchLength = -1;

    const consider = (candidatePath: string, entry: ScreenRouteEntry | ScreenSubRouteEntry) => {
        const pattern = toAbsolute(candidatePath);
        if (matchPath({ path: pattern, end: true }, pathname) && pattern.length > matchLength) {
            match = entry;
            matchLength = pattern.length;
        }
    };

    screens.forEach((screen) => {
        if (!screen?.path) return;
        consider(screen.path, screen);
        screen.subRoutes?.forEach((subRoute) => {
            if (subRoute?.suffix) consider(`${screen.path}/${subRoute.suffix}`, subRoute);
        });
    });

    return match;
};
